import { auth } from "./firebase";

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:4000";

// ── Helper: authenticated fetch ───────────────────────────
async function apiFetch(path: string, options: RequestInit = {}) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return res.json();
}

// ── Auth ──────────────────────────────────────────────────
export const authApi = {
  me:     ()                       => apiFetch("/api/auth/me"),
  update: (data: Record<string, unknown>) => apiFetch("/api/auth/me", { method: "PATCH", body: JSON.stringify(data) }),
};

// ── Clients ───────────────────────────────────────────────
export const clientsApi = {
  list:   ()           => apiFetch("/api/clients"),
  get:    (id: string) => apiFetch(`/api/clients/${id}`),
  create: (data: Record<string, unknown>) =>
    apiFetch("/api/clients", { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch(`/api/clients/${id}`, { method: "PATCH", body: JSON.stringify(data) }),

  // Measurements
  getMeasurements: (id: string) => apiFetch(`/api/clients/${id}/measurements`),
  addMeasurement:    (id: string, data: Record<string, unknown>) =>
    apiFetch(`/api/clients/${id}/measurements`, { method: "POST", body: JSON.stringify(data) }),
  updateMeasurement: (id: string, measId: string, data: Record<string, unknown>) =>
    apiFetch(`/api/clients/${id}/measurements/${measId}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteMeasurement: (id: string, measId: string) =>
    apiFetch(`/api/clients/${id}/measurements/${measId}`, { method: "DELETE" }),

  // Diet plan
  getDietPlan:      (id: string) => apiFetch(`/api/clients/${id}/dietplan`),
  saveDietPlanWeek: (id: string, week: string, data: Record<string, unknown>) =>
    apiFetch(`/api/clients/${id}/dietplan/${encodeURIComponent(week)}`, { method: "PUT", body: JSON.stringify(data) }),
  completeDietPlan: (id: string) =>
    apiFetch(`/api/clients/${id}/dietplan/complete`, { method: "POST" }),
  getDietHistory:   (id: string) => apiFetch(`/api/clients/${id}/diethistory`),
};

// ── Me (client self-service) ──────────────────────────────
export const meApi = {
  profile:         () => apiFetch("/api/auth/me"),
  getMeasurements: () => apiFetch("/api/auth/measurements"),
  getDietPlan:     () => apiFetch("/api/auth/dietplan"),
  getAppointments: () => apiFetch("/api/auth/appointments"),
  saveSelections:  (week: string, selections: Record<string, number[]>) =>
    apiFetch(`/api/auth/dietplan/${encodeURIComponent(week)}/selections`, {
      method: "PATCH", body: JSON.stringify({ selections }),
    }),
};

// ── Food library ──────────────────────────────────────────
export const foodsApi = {
  list:        ()                                                    => apiFetch("/api/foods"),
  add:         (data: Record<string, unknown>)                       => apiFetch("/api/foods", { method: "POST", body: JSON.stringify(data) }),
  update:      (id: string, data: Record<string, unknown>)           => apiFetch(`/api/foods/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  remove:      (id: string)                                          => apiFetch(`/api/foods/${id}`, { method: "DELETE" }),
  listHistory: ()                                                    => apiFetch("/api/foods/history"),
  saveHistory: (data: Record<string, unknown>)                       => apiFetch("/api/foods/history", { method: "POST", body: JSON.stringify(data) }),
};

// ── Slots ─────────────────────────────────────────────────
export const slotsApi = {
  list:        (date?: string) => apiFetch(`/api/slots${date ? `?date=${date}` : ""}`),
  dates:       ()              => apiFetch("/api/slots/dates"),
  available:   ()              => apiFetch("/api/slots/available"),
  book:        (id: string, type: string) =>
    apiFetch(`/api/slots/${id}/book`, { method: "POST", body: JSON.stringify({ type }) }),
  cancel:      (id: string) =>
    apiFetch(`/api/slots/${id}/cancel`, { method: "POST" }),
  create:      (data: Record<string, unknown>) =>
    apiFetch("/api/slots", { method: "POST", body: JSON.stringify(data) }),
  update:      (id: string, data: Record<string, unknown>) =>
    apiFetch(`/api/slots/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  remove:      (id: string) =>
    apiFetch(`/api/slots/${id}`, { method: "DELETE" }),
};
